import { differenceInYears, format } from 'date-fns';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import { deleteTournamentPlayerApplication } from 'modules/teams/store/players/actions';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { AppDispatch } from 'store/store';
import { UDImage } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import { DivProps } from 'typings';

type PlayerCardHeaderProps = {
  teamId: number;
  player: TeamPlayer;
  containerProps?: Omit<DivProps, 'children'>;
  index: number;
};

const PlayerCardHeader = (props: PlayerCardHeaderProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const open = useCallback(() => setIsModalOpen(true), []);
  const close = useCallback(() => setIsModalOpen(false), []);
  // Локальный флаг: общий на весь слайс крутил спиннер сразу на всех карточках.
  const [isLoading, setIsLoading] = useState(false);

  const { teamId, player, index } = props;

  const name = [player.lastName, player.firstName, player.middleName].filter(Boolean).join(' ');
  const birthDate = player.birthDate ? new Date(player.birthDate) : null;
  const birthDateString = birthDate ? format(birthDate, 'dd.MM.yyyy') : '';
  const years = birthDate ? differenceInYears(new Date(), birthDate) : null;
  const yearsString = years ? t('ages', { count: years }) : '';
  const fullBirthDate = [birthDateString, yearsString].filter(Boolean).join(', ') || '-';

  const handleKick = useCallback(async () => {
    setIsLoading(true);

    try {
      await dispatch(deleteTournamentPlayerApplication({ userId: player.userId, teamId })).unwrap();

      toast.success('Игрок успешно исключен');
      close();
    } catch (e) {
      toast.error('Не удалось исключить игрока');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, player.userId, teamId, close]);

  const handleClickEdit = useCallback(() => {
    navigate(Pages.PLAYERS.builders.view(player.userId), { state: { fromTeam: teamId } });
  }, [navigate, player.userId, teamId]);

  return (
    <>
      <div className={'bordered-block flex h-18 items-center p-4'}>
        <div className={'flex w-1/3 items-center'}>
          {index !== undefined && (
            <UDText type="subhead" className="bold flex min-w-8 justify-center">
              {index + 1}
            </UDText>
          )}

          <div className="flex min-w-0 items-center gap-2">
            <UDImage url={player?.avatar ?? USER_DEFAULT_AVATAR} />
            <UDText type="subhead" className="bold truncate">
              {name}
            </UDText>
          </div>
        </div>

        <div className={'flex w-1/3 items-center justify-center'}>
          <UDText type="subhead">{fullBirthDate}</UDText>
        </div>

        <div className={'flex w-1/3 items-center justify-end gap-2'}>
          <UDButton className="flex items-center" variant="primary" onClick={handleClickEdit}>
            <span>Редактировать</span>
          </UDButton>

          <UDButton className="flex items-center" variant="secondary" onClick={open}>
            <span>Исключить</span>
          </UDButton>
        </div>
      </div>

      <Modal show={isModalOpen} onClose={close}>
        <Modal.Body>
          <UDText type="headline" className="text-surfaceRomanSilver30 px-10" style={{ textAlign: 'center' }}>
            Вы действительно хотите исключить игрока
          </UDText>
          <UDText type="headline" className="text-surfaceRomanSilver30 mb-15 px-10" style={{ textAlign: 'center' }}>
            {player.firstName} {player.lastName} {player.middleName ?? ''}?
          </UDText>
        </Modal.Body>
        <Modal.Footer>
          <UDButton disabled={isLoading} className="mb-3 flex items-center justify-center" variant="primary" onClick={close} stretch>
            <span>Нет</span>
          </UDButton>
          <UDButton
            disabled={isLoading}
            loading={isLoading}
            className="mb-3 flex items-center justify-center"
            variant="primary"
            onClick={handleKick}
            stretch
          >
            <span>Да</span>
          </UDButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PlayerCardHeader;
